import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FontWeightSemiBold } from 'autogen/design-tokens/tokens';
import type { MenuItemViewModel } from 'autogen/swagger/all';
import { News } from 'autogen/translation-keys/trans-website-shared';
import Tag from 'components/generic/tag';
import WithIcon from 'components/hoc/withIcon/WithIcon';
import ListItem from 'components/List/components/ListItem/ListItem';
import type { ListItemType } from 'components/List/shared/types';

export interface IMenuItemStyleRenderer extends ListItemType {
    styling: MenuItemViewModel['styling'];
    children: ReactNode;
}

const StyledListItem = styled(ListItem)`
    font-weight: ${FontWeightSemiBold};
`;

const RedListItem = styled(ListItem)`
    color: ${({ theme }) => theme.ColorTextSale};
`;

function HighlightedListItem({ children, ...restProps }) {
    return <StyledListItem {...restProps}>{children}</StyledListItem>;
}

function NewTagListItem({ children, ...restProps }) {
    const { t } = useTranslation();

    return (
        <ListItem {...restProps}>
            <WithIcon position="right" icon={<Tag text={t(News)} />}>
                {children}
            </WithIcon>
        </ListItem>
    );
}

/**
 * Abstraction on top of ListItem, to render styling variations of ListItem that can come from MenuItemViewModel
 */
export default function MenuItemStyleRenderer({ styling, children, ...restProps }: IMenuItemStyleRenderer) {
    switch (styling) {
        case 'Highlighted':
            return <HighlightedListItem {...restProps}>{children}</HighlightedListItem>;
        case 'New':
            return <NewTagListItem {...restProps}>{children}</NewTagListItem>;
        case 'ColorRed':
            return <RedListItem {...restProps}>{children}</RedListItem>;
        default:
            return <ListItem {...restProps}>{children}</ListItem>;
    }
}
